import { Helmet } from 'react-helmet-async';
import { resolveImagePath } from '@/utils/imageMap';
import { Header } from '@/components/layout/Header';
import { HLSVideo } from '@/components/ui/HLSVideo';

export function Index() {
  const heroImage = resolveImagePath('/images/hero-orchestra.jpg');
  const harmonyRiver = resolveImagePath('/images/harmony-river-event.png');
  const musicMulligans = resolveImagePath('/images/music-mulligans.jpg');
  const princessBride = resolveImagePath('/images/princess-bride.png');
  // const educationBg = resolveImagePath('/images/education-background.jpg'); // Not used anymore
  
  const instagram1 = resolveImagePath('/images/instagram-1.jpeg');
  const instagram2 = resolveImagePath('/images/instagram-2.jpeg');
  const instagram3 = resolveImagePath('/images/instagram-3.jpeg');
  const instagram4 = resolveImagePath('/images/instagram-4.jpeg');
  const instagram5 = resolveImagePath('/images/instagram-5.jpeg');
  const instagram6 = resolveImagePath('/images/instagram-6.jpeg');
  const freeFamilyConcert = resolveImagePath('/images/free-family-concert.png');
  
  const partnerNacNea = resolveImagePath('/images/partner-nac-nea.jpg');
  const partnerElCord = resolveImagePath('/images/partner-el-cord.png');
  const partnerRedfield = resolveImagePath('/images/partner-redfield.png');
  const partnerRpet = resolveImagePath('/images/partner-rpet.png');
  const subscriberBg = resolveImagePath('/images/subscriber-background.jpg');
  const educationSide = resolveImagePath('/images/education-side-image.jpg');
  const giveBg = resolveImagePath('/images/give-background.jpg');

  return (
    <>
      <Helmet>
        <title>The Reno Phil</title>
        <meta name="description" content="The Reno Phil is northern Nevada's largest performing arts organization with something for all tastes from Classical to Pop." />
        <meta property="og:site_name" content="The Reno Phil" />
        <meta property="og:title" content="The Reno Phil" />
        <meta property="og:url" content="https://renophil.com" />
        <meta property="og:type" content="website" />
        <meta property="og:description" content="The Reno Phil is northern Nevada's largest performing arts organization with something for all tastes from Classical to Pop." />
        <meta property="og:image" content="http://static1.squarespace.com/static/640a88822000af1f3c926a4a/t/64dcd9d4127c5513aef1f7dc/1692195284889/Phil_logo_color-NEW2020-final.jpg?format=1500w" />
        <meta name="twitter:title" content="The Reno Phil" />
        <meta name="twitter:description" content="The Reno Phil is northern Nevada's largest performing arts organization with something for all tastes from Classical to Pop." />
        <meta name="twitter:card" content="summary" />
        <link rel="canonical" href="https://renophil.com" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap" rel="stylesheet" />
      </Helmet>
      
      <Header />
      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="relative h-screen pt-20">
          <div className="absolute inset-0">
            {/* Hero Video Background with HLS support */}
            <HLSVideo
              src="https://video.squarespace-cdn.com/content/v1/640a88822000af1f3c926a4a/535f8825-56fe-4f96-aefd-8c8c441faa26/playlist.m3u8"
              poster={heroImage}
              autoPlay={true}
              muted={true}
              loop={true}
              playsInline={true}
              className="w-full h-full object-cover absolute top-0 left-0"
              onError={(_error) => {
                console.warn('Hero video failed to load, using poster image instead');
                // The poster attribute will handle the fallback automatically
              }}
            />
            <div className="absolute inset-0 bg-black bg-opacity-40 z-10"></div>
          </div>
          <div className="relative z-10 flex items-center justify-center h-full text-center text-white">
            <div className="max-w-4xl px-4">
              <h1 className="text-6xl font-bold mb-6">Explore Reno Phil Season 57</h1>
              <a 
                href="/classix-57-intro" 
                className="inline-block bg-slate-700 hover:bg-slate-800 text-white font-buttons font-semibold py-3 px-8 rounded transition-colors"
              >
                Learn more
              </a>
            </div>
          </div>
        </section>

        {/* Upcoming Concerts Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-4xl font-bold text-center mb-12">UPCOMING CONCERTS AND EVENTS</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {/* Harmony on the River */}
              <div className="relative group cursor-pointer">
                <img src={harmonyRiver} alt="Harmony on the River" className="w-full h-64 object-cover rounded-lg" />
              </div>

              {/* Music and Mulligans */}
              <div className="relative group cursor-pointer">
                <img src={musicMulligans} alt="Music and Mulligans" className="w-full h-64 object-cover rounded-lg" />
              </div>

              {/* Free Family Concert */}
              <div className="relative group cursor-pointer">
                <img src={freeFamilyConcert} alt="Free Family Concert" className="w-full h-64 object-cover rounded-lg" />
              </div>

              {/* The Princess Bride */}
              <div className="relative group cursor-pointer">
                <img src={princessBride} alt="The Princess Bride" className="w-full h-64 object-cover rounded-lg" />
              </div>
            </div>
            <div className="text-center">
              <a 
                href="/calendar" 
                className="inline-block bg-blue-400 hover:bg-blue-500 text-white font-semibold py-3 px-8 rounded-lg transition-colors"
              >
                View More
              </a>
            </div>
          </div>
        </section>

        {/* Subscription Section */}
        <section className="py-16 bg-slate-100">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <h2 className="text-4xl font-bold mb-6">BECOME A SUBSCRIBER TODAY!</h2>
                <p className="text-lg mb-8">
                  We are excited to announce our 57th season of Classix concerts for the 2025-2026 season, which kicks off in October 2025. 
                  Once again, we'll offer a 6-concert Classix Series, filled with your favorite...
                </p>
                <a 
                  href="/subscribe-2025" 
                  className="inline-block bg-slate-700 hover:bg-slate-800 text-white font-semibold py-3 px-8 rounded transition-colors"
                >
                  Subscribe Today!
                </a>
              </div>
              <div className="order-1 lg:order-2">
                <img 
                  src={subscriberBg}
                  alt="Reno Philharmonic Orchestra Performance"
                  className="w-full h-96 object-cover rounded-lg"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Education Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <img 
                  src={educationSide}
                  alt="Reno Phil Education Programs"
                  className="w-full h-96 object-cover rounded-lg"
                />
              </div>
              <div>
                <h2 className="text-4xl font-bold mb-6">Education & Community</h2>
                <p className="text-lg mb-8">
                  The Reno Philharmonic supports diverse music education and community engagement programming, 
                  these programs and events serve over 22,000 students and families.
                </p>
                <a 
                  href="/education" 
                  className="inline-block bg-slate-700 hover:bg-slate-800 text-white font-buttons font-semibold py-3 px-8 rounded transition-colors"
                >
                  LEARN MORE
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Give Section */}
        <section className="give-section py-16 text-white relative overflow-hidden">
          {/* Background image with purple overlay */}
          <div className="absolute inset-0">
            <img 
              src={giveBg}
              alt="Orchestra performance"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-purple-600" style={{ opacity: 0.75 }}></div>
          </div>
          <div className="max-w-7xl mx-auto px-6 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
              <div className="flex justify-end">
                <h2 className="text-8xl md:text-9xl lg:text-[12rem] xl:text-[14rem] font-light tracking-wide leading-none mb-4">GIVE</h2>
              </div>
              <div>
                <p className="text-lg mb-8 leading-relaxed">
                  Your generosity makes it possible to bring the joy of music to the Reno/Tahoe area and beyond through innovative live and digital performances, events, and music education programs.
                </p>
                <a 
                  href="https://renophil.my.salesforce-sites.com/donate/?_ga=2.157606202.1864340384.1680812431-574965335.1673295418&dfId=a0n3r00000SIWfTAAX"
                  className="inline-block bg-black hover:bg-gray-800 text-white font-semibold py-3 px-8 rounded transition-colors"
                >
                  GIVE TODAY
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Social Follow Section */}
        <section className="py-16 bg-gray-100">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <h4 className="text-2xl font-bold mb-8">Follow us @therenophil</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <a href="https://www.instagram.com/p/DNgmBQ4AG_S/" className="block">
                <img src={instagram1} alt="Instagram post" className="w-full aspect-square object-cover rounded" />
              </a>
              <a href="https://www.instagram.com/p/DMYZAcSyS_v/" className="block">
                <img src={instagram2} alt="Instagram post" className="w-full aspect-square object-cover rounded" />
              </a>
              <a href="https://www.instagram.com/p/DMN0EgkBe_X/" className="block">
                <img src={instagram3} alt="Instagram post" className="w-full aspect-square object-cover rounded" />
              </a>
              <a href="https://www.instagram.com/p/DMBH9_hBFCx/" className="block">
                <img src={instagram4} alt="Instagram post" className="w-full aspect-square object-cover rounded" />
              </a>
              <a href="https://www.instagram.com/p/DL-rrh9h3k9/" className="block">
                <img src={instagram5} alt="Instagram post" className="w-full aspect-square object-cover rounded" />
              </a>
              <a href="https://www.instagram.com/p/DL5NxXiBz0O/" className="block">
                <img src={instagram6} alt="Instagram post" className="w-full aspect-square object-cover rounded" />
              </a>
            </div>
            <div className="text-sm text-gray-600 mb-8">
              <p>Thank you to our Corporate & COMMUNITY PARTNERS</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 justify-items-center items-center">
              <img src={partnerNacNea} alt="Nevada Arts Council & National Endowment for the Arts" className="h-16 object-contain" />
              <img src={partnerElCord} alt="El Cord de Reno" className="h-16 object-contain" />
              <img src={partnerRedfield} alt="Redfield" className="h-16 object-contain" />
              <img src={partnerRpet} alt="RPET" className="h-16 object-contain" />
            </div>
          </div>
        </section>

        {/* Newsletter Section */}
        <section className="py-16 bg-black text-white">
          <div className="max-w-4xl mx-auto px-4">
            <form className="max-w-md mx-auto">
              <h2 className="text-3xl font-bold mb-4">Join our list</h2>
              <p className="mb-6">Subscribe and be in the loop with upcoming events and projects</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <input 
                  type="text" 
                  placeholder="First Name" 
                  className="px-4 py-2 rounded text-black"
                />
                <input 
                  type="text" 
                  placeholder="Last Name" 
                  className="px-4 py-2 rounded text-black"
                />
              </div>
              <input 
                type="email" 
                placeholder="Email Address" 
                className="w-full px-4 py-2 rounded text-black mb-4"
              />
              <button 
                type="submit" 
                className="w-full bg-slate-700 hover:bg-slate-800 text-white font-buttons font-semibold py-3 rounded transition-colors"
              >
                Sign Up
              </button>
              <p className="text-sm mt-4 text-gray-400">We respect your privacy.</p>
            </form>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-black text-white py-12">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <h5 className="font-bold mb-4">BOX OFFICE ADDRESS</h5>
                <p>925 Riverside Drive, Suite 3<br />Reno, NV 89503<br />(775) 323-6393</p>
                <p className="mt-2"><strong>HOURS</strong><br />Monday - Friday, 10AM - 3PM</p>
              </div>
              <div>
                <div className="flex space-x-4 mb-4">
                  <a href="https://www.instagram.com/therenophil/" className="text-white hover:text-gray-300">
                    Instagram
                  </a>
                  <a href="https://www.facebook.com/RenoPhil" className="text-white hover:text-gray-300">
                    Facebook
                  </a>
                  <a href="https://www.youtube.com/therenophil" className="text-white hover:text-gray-300">
                    YouTube
                  </a>
                </div>
                <div className="space-y-2">
                  <a href="/subscribe" className="block text-white hover:text-gray-300">BUY TICKETS</a>
                  <a href="/faqs" className="block text-white hover:text-gray-300">GETTING HERE</a>
                  <a href="/about-the-reno-phil" className="block text-white hover:text-gray-300">ABOUT</a>
                  <a href="/contact-us" className="block text-white hover:text-gray-300">CONTACT US</a>
                </div>
              </div>
              <div>
                <p className="text-sm">
                  This project is supported in part by the Nevada Arts Council and the National Endowment for the Arts.
                </p>
                <p className="text-xs mt-4">
                  THE RENO PHILHARMONIC IS A FEDERALLY REGISTERED 501(C)(3) NONPROFIT ORGANIZATION IN THE STATE OF NEVADA, 
                  TAX ID #94-2762076.© 2023 The Reno Philharmonic. Website by 
                  <a href="https://mrdcreativeagency.com" className="text-white hover:text-gray-300">MRD • A Creative Agency</a>
                </p>
                <a href="/privacy-policy" className="block mt-2 text-white hover:text-gray-300">PRIVACY POLICY</a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}